import {
  type IconName,
  SelectField,
  type SelectFieldOption,
} from '@wandb/components';
import React, {useEffect, useMemo, useRef} from 'react';

import {type Config as TableSettings} from '../../../util/runfeed';
import {keyDisplayName, keyFromString, keyToString} from '../../../util/runs';
import type * as RunTypes from '../../../util/runTypes';
import {getIconNameForKey} from '../../../util/uihelpers';
import {type FilterKeySelectorCreatorProps} from '../../Filters/FilterKeySelector';
import {useProjectFieldSelectorProps} from '../../ProjectFieldSelector';
import {RUNS_FILTER_DEFAULT_KEYS} from './constants';

type RunsFilterKeySelectorProps = FilterKeySelectorCreatorProps & {
  entityName: string;
  projectName: string;
  tableSettings?: TableSettings;
  /** Keys offered ahead of the project's own fields. */
  defaultKeys?: string[];
};

/**
 * The library's icon set has every name `getIconNameForKey` returns except
 * `list`, which it spells `list-bullets`.
 */
const keyIcon = (key: RunTypes.Key): IconName => {
  const name = getIconNameForKey(key);
  return name === 'list' ? 'list-bullets' : (name as IconName);
};

/**
 * The stored key arrives as `displayKey` output: a bare name for run keys,
 * `section:name` otherwise, and "-" for an empty row. Options are keyed by
 * the canonical `section:name` form so the two can be compared.
 */
const toOptionId = (keyValue: string): string | null => {
  if (keyValue === '' || keyValue === '-') {
    return null;
  }
  const key = keyFromString(keyValue);
  return key == null ? null : keyToString(key);
};

const toOption = (keyString: string): SelectFieldOption | null => {
  const key = keyFromString(keyString);
  if (key == null) {
    return null;
  }
  return {
    id: keyToString(key),
    label: keyDisplayName(key, true),
    decorationLeft: {iconProps: {icon: keyIcon(key)}},
  };
};

/**
 * The key field of a run filter row: the project's fields, searched on the
 * server as the user types, with a few run attributes offered up front.
 */
export const RunsFilterKeySelector = ({
  entityName,
  projectName,
  tableSettings,
  defaultKeys = RUNS_FILTER_DEFAULT_KEYS,
  keyValue,
  onValidSelection,
  focusOnMount,
  isValid = true,
}: RunsFilterKeySelectorProps) => {
  const selectedId = toOptionId(keyValue);
  const {keys, loading, search, setSearch, setOpen} =
    useProjectFieldSelectorProps({
      entityName,
      projectName,
      tableSettings,
      defaultKeys,
      multi: false,
      value: selectedId ?? undefined,
      setValue: onValidSelection,
      searchByKeyAndText: true,
    });

  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: SelectFieldOption[] = [];
    for (const keyString of keys) {
      const option = toOption(keyString);
      if (option != null && !seen.has(String(option.id))) {
        seen.add(String(option.id));
        out.push(option);
      }
    }
    return out;
  }, [keys]);

  // A row added with "New filter" starts on its key field, as it did when the
  // legacy dropdown autofocused its search input. The library strips
  // autoFocus and does not expose the trigger, so it is found by its ARIA
  // contract: the select's trigger is the element that pops up the listbox.
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusOnMount) {
      wrapperRef.current
        ?.querySelector<HTMLElement>('[aria-haspopup="listbox"]')
        ?.focus();
    }
  }, [focusOnMount]);

  return (
    <div ref={wrapperRef} className="min-w-[140px] max-w-[400px]">
      <SelectField
        aria-label="Key"
        data-test="filter-key"
        placeholder="Key"
        fullWidth
        items={options}
        value={selectedId}
        onChange={key => {
          if (key != null) {
            onValidSelection(String(key));
          }
        }}
        isInvalid={!isValid}
        isLoading={loading}
        search={{value: search, onInput: setSearch, placeholder: 'Search keys'}}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          if (!isOpen) {
            setSearch('');
          }
        }}
      />
    </div>
  );
};
